import generateKey from "./utils/generateKey";
import timeout from "./utils/timeout";
import scriptObserver from "./utils/scriptObserver";
import getObjectSignature from "./utils/getObjectSignature";

const ERROR = "error";
const LOADED = "loaded";

class Spritz {

    /**
     * Map of pending requests
     * @type {Object}
     */
    requestQueue = new Map();

    /**
     * Set of loaded Scripts
     * @type {Set<string>}
     */
    loadedScripts = new Set();

    /**
     * @type {string}
     */
    version = process.env.VERSION;

    /**
     * @type {Function[]}
     */
    cmd = [];

    /**
     * @constructor
     * @param {Spritz|Object} [_stub]
     */
    constructor(_stub) {
        const stubSignature = getObjectSignature(_stub);
        const selfSignature = getObjectSignature(this);

        // Check if stub is already an instance of this class
        if (stubSignature === selfSignature) {
            return _stub;
        }

        Array.from(document.scripts).forEach((script) => {
            if (!script.matches("script[src]")) {
                return;
            }

            const scriptKey = generateKey(script.src);

            this.loadedScripts.add(scriptKey);
        });

        scriptObserver(this.loadedScripts);

        this.cmd = _stub && _stub.cmd || [];
        this.cmd.push = (cb) => {
            if (typeof cb === "function") {
                cb();
            }
        };
    }

    /**
     * @param {string} url
     * @returns {boolean}
     */
    isLoaded(url) {
        const scriptKey = generateKey(url);

        return this.loadedScripts.has(scriptKey) && !this.requestQueue.has(scriptKey);
    }

    /**
     * @param url
     * @return {boolean}
     */
    isRequested(url) {
        const scriptKey = generateKey(url);

        return this.requestQueue.has(scriptKey);
    }

    /**
     * Injects a script into a document if its not already loaded. Loaded state is determined by a central
     * store which keeps a list of all loaded scripts keyed by "domain:filename".
     * If script is 404, it will not be loaded twice. If it times out a secondary attempt would be allowed.
     * @param {string} url
     * @param {Object} [options]
     * @param {number} [options.timeout] Milliseconds
     * @param {boolean} [options.ordered=false] No Async
     * @returns {Promise}
     */
    inject(url, { timeout: ms, ordered = false } = {}) {
        const scriptKey = generateKey(url);

        if (this.isRequested(url)) {
            return this.requestQueue.get(scriptKey);
        }

        if (this.isLoaded(url)) {
            return Promise.resolve(url);
        }

        this.requestQueue.set(scriptKey, Spritz.injectScript(url, document.head, ordered)
            .then((src) => {
                this.requestQueue.delete(scriptKey);

                return src;
            }));

        if (ms) {
            return Promise.race([timeout(ms), this.requestQueue.get(scriptKey)])
                .catch(() => {
                    this.requestQueue.delete(scriptKey);
                    this.loadedScripts.delete(scriptKey);
                    throw new Error(`ScriptInjector: Timed out loading "${ url }" after ${ ms } ms`);
                });
        }

        return this.requestQueue.get(scriptKey);
    }

    /**
     * Flushes command queue
     * @private
     */
    _flushQueue() {
        if (Array.isArray(this.cmd)) {
            while (this.cmd.length) {
                const cb = this.cmd.pop();

                if (typeof cb === "function") {
                    cb();
                }
            }
        }
    }

    /**
     * @static
     * @param {string} src Url of the script
     * @param {Element} dest Element to which script will be appended
     * @param {boolean} ordered
     * @returns {Promise}
     */
    static injectScript(src, dest, ordered = false) {
        let tag = document.createElement("script");

        tag.src = src;
        tag.async = !ordered;

        return new Promise((resolve, reject) => {
            if (tag.onreadystatechange !== undefined) {
                tag.onreadystatechange = function onreadystatechange() {
                    if (LOADED === this.readyState) {
                        tag.onreadystatechange = null;
                        tag = null;
                        resolve(src);
                    }
                };
            } else {
                // eslint-disable-next-line func-name-matching, no-multi-assign
                tag.onload = tag.onerror = function onLoadOrError(event) {
                    if (event.type === ERROR) {
                        reject(`ScriptInjector: Error loading script "${ src }"`);
                        dest.removeChild(tag);
                        tag.onload = null;
                        tag.onerror = null;
                        tag = null;

                        return;
                    }

                    tag.onload = null;
                    tag.onerror = null;
                    tag = null;
                    resolve(src);
                };
            }

            dest.appendChild(tag);
        });
    }

}

export default Spritz;
